import Phaser from 'phaser';
import '@fontsource/fredoka/400.css';
import '@fontsource/fredoka/500.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/fredoka/700.css';
import '@fontsource/vt323/400.css';
import { GAME_WIDTH, GAME_HEIGHT } from './config';
import { store } from './state/store';
import { baking } from './systems/BakingSystem';
import { ovenTimers } from './systems/OvenTimers';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { HeaderBar } from './scenes/HeaderBar';
import { WelcomeScene } from './scenes/WelcomeScene';
import { MenuScene } from './scenes/MenuScene';
import { KitchenScene } from './scenes/KitchenScene';
import { OvenScene } from './scenes/OvenScene';
import { CounterScene } from './scenes/CounterScene';
import { DayEndScene } from './scenes/DayEndScene';

// Phaser renders text to canvas; the browser doesn't auto-fetch webfonts that
// are only used in canvas. Explicitly request every weight we use, then start
// Phaser. Without this, first paint uses the fallback font and never refreshes.
async function ensureFontsReady(): Promise<void> {
  const fredokaWeights = ['400', '500', '600', '700'];
  await Promise.all([
    ...fredokaWeights.map((w) => document.fonts.load(`${w} 16px Fredoka`)),
    document.fonts.load('400 16px VT323'),
  ]);
  await document.fonts.ready;
}

async function bootGame(): Promise<void> {
  await ensureFontsReady();
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    backgroundColor: '#e7e2f9',
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    // Hidden tabs never fire requestAnimationFrame, which freezes Phaser on
    // Boot. In dev (headless preview panes, background tabs) fall back to
    // setTimeout stepping so the game still runs; visible tabs keep rAF.
    fps: import.meta.env.DEV && document.hidden ? { forceSetTimeOut: true } : undefined,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    render: {
      antialias: true,
      pixelArt: false,
      roundPixels: false,
    },
    scene: [
      BootScene,
      PreloadScene,
      HeaderBar,
      WelcomeScene,
      MenuScene,
      KitchenScene,
      OvenScene,
      CounterScene,
      DayEndScene,
    ],
  });

  if (import.meta.env.DEV) {
    // Expose the game-graph instances for dev-console/preview verification.
    // Dynamic `import('/src/...')` in the console can resolve a SECOND module
    // instance once Vite hot-stamps the graph (?t=...) — always use these.
    Object.assign(window as object, { game, store, baking, ovenTimers });
  }

  if (import.meta.hot) {
    import.meta.hot.dispose(() => game.destroy(true));
  }
}

void bootGame();
